import { useCallback, useEffect, useRef, useState } from 'react'
import apiClient from './api/client'
import {
  mismosVistos, ponerAlDia, type ConMensajes, type Vistos, type VistosDelServidor,
} from './noLeidos'

/**
 * Cuántos mensajes no vio quien mira, por conversación: el número verde de la
 * lista. Las reglas están en `noLeidos.ts`; acá se guardan y se cablean a la
 * conversación abierta y a si la pestaña está a la vista.
 *
 * Lo visto es de la cuenta: llega con la lista (`servidor`) y se guarda con
 * `PUT /me/vistos`. El `localStorage` es la caché de este navegador —con el
 * correo en la clave: en una misma computadora pueden entrar dos personas—, y
 * es lo único que hay si el servidor no pudo leer lo visto.
 */
export function useNoLeidos(
  conversaciones: ReadonlyArray<ConMensajes>,
  abierta: string | null,
  email: string,
  servidor: VistosDelServidor | null = null,
): Vistos | null {
  const clave = `pgh.vistos.${email.toLowerCase()}`
  const [vistos, setVistos] = useState<Vistos | null>(() => leer(clave))
  const vistosRef = useRef(vistos)
  vistosRef.current = vistos
  // Lo mandado al servidor que la lista todavía no confirma (`porGuardar`).
  const enviados = useRef<Vistos>({})

  // Si cambia quién mira —el correo llega después del primer render—, lo
  // visto es el de esa persona. Va antes del efecto que pone al día, que lee
  // la ref.
  useEffect(() => {
    const suyos = leer(clave)
    vistosRef.current = suyos
    enviados.current = {}
    setVistos(suyos)
  }, [clave])

  const poner = useCallback((mirando: boolean) => {
    // Sin lista no hay nada que decidir, salvo que ya haya llegado —vacía—
    // con lo del servidor: ahí sí se toma nota, aunque sea de nada.
    if (!email || (conversaciones.length === 0 && !servidor)) return
    if (servidor) {
      for (const [id, n] of Object.entries(enviados.current)) {
        if (servidor.vistos[id] === n) delete enviados.current[id]
      }
    }
    const previos = vistosRef.current
    const { vistos: siguiente, envio } = ponerAlDia({
      local: previos, servidor, conversaciones, abierta, mirando, enviados: enviados.current,
    })
    if (!mismosVistos(previos, siguiente)) {
      vistosRef.current = siguiente
      setVistos(siguiente)
      try {
        window.localStorage.setItem(clave, JSON.stringify(siguiente))
      } catch {
        // Sin almacenamiento los números duran la pestaña: siguen sirviendo.
      }
    }
    if (envio) {
      Object.assign(enviados.current, envio.vistos)
      apiClient.put('/me/vistos', envio).catch(() => {
        // Se reintenta con la lista siguiente: lo no confirmado deja de contar
        // como mandado.
        for (const id of Object.keys(envio.vistos)) {
          if (enviados.current[id] === envio.vistos[id]) delete enviados.current[id]
        }
      })
    }
  }, [clave, email, conversaciones, abierta, servidor])

  // Cada lista nueva y cada cambio de conversación abierta. «Mirando» es la
  // pestaña a la vista: con el portal detrás de otra ventana, lo que llega a
  // la conversación abierta tampoco lo vio nadie.
  useEffect(() => { poner(document.visibilityState === 'visible') }, [poner])

  // Volver a la pestaña marca como visto lo que llegó a la abierta mientras
  // no se miraba: es lo que haría el ojo al volver.
  useEffect(() => {
    const alVolver = () => { if (document.visibilityState === 'visible') poner(true) }
    document.addEventListener('visibilitychange', alVolver)
    return () => document.removeEventListener('visibilitychange', alVolver)
  }, [poner])

  // Otra pestaña del portal abrió una conversación: lo visto se comparte.
  useEffect(() => {
    const alCambiar = (e: StorageEvent) => {
      if (e.key !== clave) return
      const otros = leer(clave)
      vistosRef.current = otros
      setVistos(otros)
    }
    window.addEventListener('storage', alCambiar)
    return () => window.removeEventListener('storage', alCambiar)
  }, [clave])

  return vistos
}

function leer(clave: string): Vistos | null {
  try {
    const crudo = window.localStorage.getItem(clave)
    if (!crudo) return null
    const valor: unknown = JSON.parse(crudo)
    return valor && typeof valor === 'object' && !Array.isArray(valor) ? valor as Vistos : null
  } catch {
    return null
  }
}
